import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@material-ui/core/Grid';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { NkFormDialog, NkConfirmDialog } from '@nordkap/nordkap-ui';
import _ from 'lodash';
import { v4 } from 'uuid';
import {
  eventTypeRulesAreValid,
  templateNameIsValid,
  TemplateContainer,
} from './utils';
import EventTypeForm from './eventTypeForm';
import TemplateName from './templateName';
import { emptyGuid } from '../../utils/constants';
import api from '../../infrastructure/api/api';

const TemplateDialog = props => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const templateContainer = TemplateContainer.useContainer();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogSubMessage, setConfirmDialogSubMessage] = useState('');
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogInitiator, setConfirmDialogInitiator] = useState('');

  const saveTemplate = useCallback(() => {
    if (
      eventTypeRulesAreValid(templateContainer.eventTypeRules) &&
      templateNameIsValid(templateContainer.templateName)
    ) {
      const newPositionTemplate = {
        ...props.positionTemplate,
        positionTemplateId:
          props.positionTemplate.positionTemplateId !== emptyGuid
            ? props.positionTemplate.positionTemplateId
            : v4(),
        name: templateContainer.templateName.trim(),
        rules: _.flatten(
          templateContainer.eventTypeRules.map(etr => etr.rules)
        ),
      };
      api
        .post('positionTemplates', newPositionTemplate, enqueueSnackbar, t)
        .then(result => {
          if (result.success && props.templateSaved) {
            props.templateSaved({
              archived: newPositionTemplate.archived,
              name: newPositionTemplate.name,
              positionTemplateId: newPositionTemplate.positionTemplateId,
              positionCount: props.positionCount,
            });
          }
        });
    } else {
      enqueueSnackbar(t('positionPage.invalidFields'), { variant: 'error' });
    }
  }, [templateContainer, props, enqueueSnackbar, t]);

  const openConfirmDialogOnSave = useCallback(() => {
    if (props.positionCount > 1) {
      setConfirmDialogMessage('templateDialog.saveTemplateMessage');
      setConfirmDialogSubMessage(
        'templateDialog.areYouSureYouWantToUpdateTheTemplate'
      );
      setConfirmDialogOpen(true);
      setConfirmDialogInitiator('Save');
    } else {
      saveTemplate();
    }
  }, [saveTemplate, props.positionCount]);

  const openConfirmDialogOnClose = useCallback(() => {
    if (templateContainer.dirtyState) {
      setConfirmDialogMessage('templateDialog.closeWithoutSavingMessage');
      setConfirmDialogSubMessage(
        'templateDialog.areYouSureYouWantToDiscardChangesMessage'
      );
      setConfirmDialogOpen(true);
      setConfirmDialogInitiator('Close');
    } else {
      props.closeDialog();
    }
  }, [
    setConfirmDialogSubMessage,
    setConfirmDialogOpen,
    props,
    templateContainer.dirtyState,
  ]);

  const closeOnYes = useCallback(() => {
    if (confirmDialogInitiator === 'Save') {
      saveTemplate();
    }
    setConfirmDialogOpen(false);
    props.closeDialog();
  }, [props, saveTemplate, confirmDialogInitiator]);

  const closeOnNo = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const mapRules = useCallback(
    (rules, eventTypes) =>
      eventTypes.reduce((result, eventType) => {
        const eventTypeRules = {
          eventType,
          rules: rules
            .filter(r => r.eventType === eventType)
            .reduce((rulesWithIndex, rule, index) => {
              rule.index = index;
              rulesWithIndex.push(rule);
              return rulesWithIndex;
            }, []),
        };
        result.push(eventTypeRules);
        return result;
      }, []),
    []
  );

  useEffect(() => {
    const eventTypeRules = mapRules(
      props.positionTemplate.rules,
      props.eventTypes
    );
    templateContainer.setEventTypeRules(eventTypeRules);
    templateContainer.setFieldsAndTemplates(props.fieldsAndTemplates);
    templateContainer.setTemplateName(props.positionTemplate.name);
    templateContainer.setDirtyState(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.positionTemplate, props.eventTypes, props.fieldsAndTemplates]);

  return (
    <>
      <NkConfirmDialog
        onYes={closeOnYes}
        onNo={closeOnNo}
        isOpen={confirmDialogOpen}
        message={t(confirmDialogMessage)}
        subMessage={t(confirmDialogSubMessage, {
          positionCount: t(props.positionCount),
        })}
        confirmLabel={t('global.buttons.yes')}
        cancelLabel={t('global.buttons.no')}
      />
      <NkFormDialog
        title={
          props.positionTemplate.positionTemplateId !== emptyGuid
            ? t('positionPage.editTemplateTitle', {
                positionType: t(
                  `positionTypePlural.${props.positionTemplate.positionType}`
                ),
              })
            : t('positionPage.createTemplateTitle', {
                positionType: t(
                  `positionTypePlural.${props.positionTemplate.positionType}`
                ),
              })
        }
        isOpen={props.isOpen}
        onClose={openConfirmDialogOnClose}
        closeLabelText={t('global.buttons.close')}
        onSave={openConfirmDialogOnSave}
        saveLabelText={t('global.buttons.save')}
        size="xxxl"
        width="xl"
        fullWidth
        saveDisabled={!templateContainer.dirtyState}
      >
        <Grid container>
          <TemplateName
            positionTemplate={props.positionTemplate}
            positionCount={props.positionCount}
          />
          {templateContainer.eventTypeRules.map(eventTypeRule => (
            <EventTypeForm
              key={eventTypeRule.eventType}
              eventType={eventTypeRule.eventType}
              rules={eventTypeRule.rules}
              selectOptions={props.selectOptions}
            />
          ))}
        </Grid>
      </NkFormDialog>
    </>
  );
};

TemplateDialog.propTypes = {
  positionTemplate: PropTypes.object,
  selectOptions: PropTypes.object,
  eventTypes: PropTypes.array,
  fieldsAndTemplates: PropTypes.object,
  isOpen: PropTypes.bool,
  templateSaved: PropTypes.func,
  closeDialog: PropTypes.func,
  positionCount: PropTypes.number,
};

export default React.memo(TemplateDialog);
